import React, { useState } from 'react';
import { mockAgents, Agent } from '../data/mockData';
import PageFooter from '../components/PageFooter';
import './AgentCatalog.css';

const AgentCatalog: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null);

  const categories = ['all', ...Array.from(new Set(mockAgents.map(a => a.category)))];

  const filteredAgents = mockAgents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         agent.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || agent.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <div className="agent-catalog">
      <div className="catalog-header">
        <h1>Agent Catalog</h1>
        <p>Browse and discover AI agents for your workflows</p>
      </div>

      <div className="catalog-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search agents by name, description, or tags..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="category-filter">
          {categories.map(cat => (
            <button
              key={cat}
              className={selectedCategory === cat ? 'category-btn active' : 'category-btn'}
              onClick={() => setSelectedCategory(cat)}
            >
              {cat === 'all' ? 'All Categories' : cat}
            </button>
          ))}
        </div>
      </div>

      <div className="catalog-content">
        <div className="agents-grid">
          {filteredAgents.map(agent => (
            <div
              key={agent.id}
              className="agent-card"
              onClick={() => setSelectedAgent(agent)}
            >
              <div className="agent-card-header">
                <h3>{agent.name}</h3>
                <span className={`status-badge ${agent.status}`}>{agent.status}</span>
              </div>
              <p className="agent-version">v{agent.version}</p>
              <p className="agent-description">{agent.description}</p>
              <div className="agent-tags">
                {agent.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
              <div className="agent-footer">
                <span className="agent-category">{agent.category}</span>
                <span className="agent-usage">{agent.usageCount} uses</span>
              </div>
            </div>
          ))}
        </div>

        {selectedAgent && (
          <div className="agent-details-modal" onClick={() => setSelectedAgent(null)}>
            <div className="agent-details" onClick={(e) => e.stopPropagation()}>
              <div className="details-header">
                <h2>{selectedAgent.name}</h2>
                <button className="close-btn" onClick={() => setSelectedAgent(null)}>×</button>
              </div>
              
              <div className="details-content">
                <div className="detail-section">
                  <h3>Overview</h3>
                  <p><strong>Version:</strong> {selectedAgent.version}</p>
                  <p><strong>Category:</strong> {selectedAgent.category}</p>
                  <p><strong>Owner:</strong> {selectedAgent.owner}</p>
                  <p><strong>Status:</strong> <span className={`status-badge ${selectedAgent.status}`}>{selectedAgent.status}</span></p>
                  <p><strong>Description:</strong> {selectedAgent.description}</p>
                </div>

                <div className="detail-section">
                  <h3>Technical Details</h3>
                  <p><strong>Endpoint:</strong> <code>{selectedAgent.endpoint}</code></p>
                  <p><strong>Authentication:</strong> {selectedAgent.authMethod}</p>
                  <p><strong>Rate Limit:</strong> {selectedAgent.rateLimit}</p>
                  <p><strong>SLA:</strong> {selectedAgent.sla}</p>
                </div>

                <div className="detail-section">
                  <h3>Schema</h3>
                  <p><strong>Input:</strong></p>
                  <pre>{JSON.stringify(selectedAgent.inputSchema, null, 2)}</pre>
                  <p><strong>Output:</strong></p>
                  <pre>{JSON.stringify(selectedAgent.outputSchema, null, 2)}</pre>
                </div>

                <div className="detail-section">
                  <h3>Usage Statistics</h3>
                  <p><strong>Total Executions:</strong> {selectedAgent.usageCount}</p>
                </div>

                <div className="detail-section">
                  <h3>Tags</h3>
                  <div className="agent-tags">
                    {selectedAgent.tags.map(tag => (
                      <span key={tag} className="tag">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="details-footer">
                <button className="btn-secondary" onClick={() => setSelectedAgent(null)}>Close</button>
                <button className="btn-primary">Use in Workflow</button>
              </div>
            </div>
          </div>
        )}
      </div>
      <PageFooter />
    </div>
  );
};

export default AgentCatalog;

